import { useMemo, useState } from 'react';
import type { Activity } from '../../types/schedule';
import { ChartEmpty, formatDate, useMeasure, useTooltip } from './chartPrimitives';

interface ScheduleGanttProps {
  activities: Activity[];
  baselineByCode?: Map<string, Activity>;
  dataDate: Date | null;
  onSelectActivity?: (activityId: string) => void;
  selectedActivityId?: string | null;
}

const ROW_HEIGHT = 24;
const LABEL_WIDTH = 230;
const HEADER_HEIGHT = 34;
const VIEWPORT_HEIGHT = 460;
const MS_PER_DAY = 86_400_000;

/**
 * Gantt with baseline ghosts, float tails and milestone diamonds.
 *
 * Rows are virtualised: a 10,000-activity programme renders only the ~20 rows
 * in view, so the chart stays interactive on a real schedule rather than a
 * demo-sized one.
 */
export function ScheduleGantt({
  activities,
  baselineByCode,
  dataDate,
  onSelectActivity,
  selectedActivityId,
}: ScheduleGanttProps) {
  const [containerRef, { width: outerWidth }] = useMeasure<HTMLDivElement>();
  // Measure the scroll viewport, not the outer box: once the vertical
  // scrollbar appears it takes ~11px out of the content area, and sizing the
  // SVG to the outer width overflows by exactly that much, which is what
  // produces a spurious horizontal scrollbar.
  const [viewportRef, { width: viewportWidth }] = useMeasure<HTMLDivElement>();
  const [scrollTop, setScrollTop] = useState(0);
  const tooltip = useTooltip();

  const width = viewportWidth || outerWidth;

  const dated = useMemo(
    () => activities.filter((a) => a.start && a.finish),
    [activities]
  );

  const bounds = useMemo(() => {
    if (dated.length === 0) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const activity of dated) {
      min = Math.min(min, activity.start!.getTime());
      max = Math.max(max, activity.finish!.getTime());
      // Float tails extend past the finish, so include them in the span.
      if (activity.totalFloatDays > 0 && !activity.isComplete) {
        max = Math.max(
          max,
          activity.finish!.getTime() + activity.totalFloatDays * MS_PER_DAY
        );
      }
      const baseline = baselineByCode?.get(activity.code);
      if (baseline?.start) min = Math.min(min, baseline.start.getTime());
      if (baseline?.finish) max = Math.max(max, baseline.finish.getTime());
    }
    // A little breathing room either end.
    const pad = (max - min) * 0.02;
    return { min: min - pad, max: max + pad };
  }, [dated, baselineByCode]);

  if (dated.length === 0 || !bounds) {
    return <ChartEmpty message="No activities with both start and finish dates." />;
  }

  const chartWidth = Math.max(0, width - LABEL_WIDTH);
  const span = bounds.max - bounds.min || 1;
  const xFor = (time: number) => ((time - bounds.min) / span) * chartWidth;

  const totalHeight = dated.length * ROW_HEIGHT;
  const firstVisible = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - 4);
  const lastVisible = Math.min(
    dated.length,
    Math.ceil((scrollTop + VIEWPORT_HEIGHT) / ROW_HEIGHT) + 4
  );
  const visible = dated.slice(firstVisible, lastVisible);

  // Year/quarter ticks across the top.
  const ticks: { time: number; label: string; major: boolean }[] = [];
  const cursor = new Date(bounds.min);
  cursor.setDate(1);
  const spanYears = span / (365 * MS_PER_DAY);
  const monthStep = spanYears > 8 ? 12 : spanYears > 3 ? 6 : spanYears > 1.5 ? 3 : 1;
  cursor.setMonth(Math.floor(cursor.getMonth() / monthStep) * monthStep);

  while (cursor.getTime() <= bounds.max && ticks.length < 200) {
    ticks.push({
      time: cursor.getTime(),
      label:
        monthStep >= 12
          ? String(cursor.getFullYear())
          : cursor.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' }),
      major: cursor.getMonth() === 0,
    });
    cursor.setMonth(cursor.getMonth() + monthStep);
  }

  const truncate = (text: string, max: number) =>
    text.length > max ? `${text.slice(0, max - 1)}…` : text;

  return (
    <div className="w-full">
      <div ref={containerRef} className="w-full">
        {width > 0 && (
          <>
            {/* Fixed header: timescale */}
            <svg width={width} height={HEADER_HEIGHT} style={{ display: 'block' }}>
              <rect width={width} height={HEADER_HEIGHT} fill="var(--surface-card)" />
              <g transform={`translate(${LABEL_WIDTH},0)`}>
                {ticks.map((tick) => (
                  <g key={tick.time}>
                    <line
                      x1={xFor(tick.time)}
                      x2={xFor(tick.time)}
                      y1={HEADER_HEIGHT - 8}
                      y2={HEADER_HEIGHT}
                      stroke="var(--border-strong)"
                    />
                    <text
                      x={xFor(tick.time) + 4}
                      y={HEADER_HEIGHT - 12}
                      className="chart-axis-label"
                      style={{ fill: tick.major ? 'var(--text-secondary)' : 'var(--text-muted)' }}
                    >
                      {tick.label}
                    </text>
                  </g>
                ))}
                {dataDate && (
                  <text
                    x={xFor(dataDate.getTime()) + 4}
                    y={11}
                    className="chart-axis-label"
                    style={{ fill: 'var(--mr-vivid-blue)' }}
                  >
                    DATA DATE
                  </text>
                )}
              </g>
              <line
                x1={0}
                x2={width}
                y1={HEADER_HEIGHT - 0.5}
                y2={HEADER_HEIGHT - 0.5}
                stroke="var(--border-strong)"
              />
            </svg>

            {/* Scrolling body */}
            <div
              ref={viewportRef}
              className="w-full overflow-y-auto"
              style={{ height: Math.min(VIEWPORT_HEIGHT, totalHeight + 8) }}
              onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            >
              <svg width={width} height={totalHeight} style={{ display: 'block' }}>
                <g transform={`translate(${LABEL_WIDTH},0)`}>
                  {ticks.map((tick) => (
                    <line
                      key={tick.time}
                      x1={xFor(tick.time)}
                      x2={xFor(tick.time)}
                      y1={0}
                      y2={totalHeight}
                      className="chart-grid-line"
                    />
                  ))}
                  {dataDate && (
                    <line
                      x1={xFor(dataDate.getTime())}
                      x2={xFor(dataDate.getTime())}
                      y1={0}
                      y2={totalHeight}
                      stroke="var(--mr-vivid-blue)"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                  )}
                </g>

                {visible.map((activity, index) => {
                  const rowIndex = firstVisible + index;
                  const y = rowIndex * ROW_HEIGHT;
                  const isSelected = activity.id === selectedActivityId;
                  const baseline = baselineByCode?.get(activity.code);

                  const startX = xFor(activity.start!.getTime());
                  const endX = xFor(activity.finish!.getTime());
                  const barWidth = Math.max(2, endX - startX);

                  const barColour = activity.isComplete
                    ? 'var(--status-neutral)'
                    : activity.totalFloatDays < 0
                      ? 'var(--status-fail)'
                      : activity.isCritical
                        ? 'var(--mr-vivid-blue)'
                        : 'var(--data-4)';

                  return (
                    <g
                      key={activity.id}
                      className="chart-hit"
                      onMouseMove={(event) =>
                        tooltip.show(
                          <div>
                            <div className="font-semibold">{activity.code}</div>
                            <div style={{ color: 'var(--text-secondary)' }}>
                              {activity.name}
                            </div>
                            <div className="tnum" style={{ marginTop: 4 }}>
                              {formatDate(activity.start)} → {formatDate(activity.finish)}
                            </div>
                            {baseline && (
                              <div className="tnum" style={{ color: 'var(--text-muted)' }}>
                                Baseline: {formatDate(baseline.start)} →{' '}
                                {formatDate(baseline.finish)}
                              </div>
                            )}
                            <div className="tnum">
                              {activity.totalFloatDays.toFixed(0)}d float ·{' '}
                              {activity.percentComplete.toFixed(0)}% complete
                            </div>
                          </div>,
                          event
                        )
                      }
                      onMouseLeave={tooltip.hide}
                      onClick={() => onSelectActivity?.(activity.id)}
                    >
                      <rect
                        x={0}
                        y={y}
                        width={width}
                        height={ROW_HEIGHT}
                        fill={
                          isSelected
                            ? 'rgba(0,115,255,0.1)'
                            : rowIndex % 2 === 0
                              ? 'transparent'
                              : 'var(--surface-inset)'
                        }
                      />
                      <text
                        x={8}
                        y={y + ROW_HEIGHT / 2 + 3.5}
                        style={{
                          fill: isSelected ? 'var(--mr-vivid-blue)' : 'var(--text-secondary)',
                          fontSize: 10.5,
                        }}
                      >
                        {truncate(`${activity.code}  ${activity.name}`, 34)}
                      </text>

                      <g transform={`translate(${LABEL_WIDTH},0)`}>
                        {/* Baseline ghost, sitting under the live bar */}
                        {baseline?.start && baseline.finish && (
                          <rect
                            x={xFor(baseline.start.getTime())}
                            y={y + ROW_HEIGHT / 2 - 1}
                            width={Math.max(
                              2,
                              xFor(baseline.finish.getTime()) -
                                xFor(baseline.start.getTime())
                            )}
                            height={3}
                            fill="var(--text-muted)"
                            opacity={0.5}
                            rx={1}
                          />
                        )}

                        {/* Float tail */}
                        {!activity.isComplete && activity.totalFloatDays > 0 && (
                          <line
                            x1={endX}
                            x2={xFor(
                              activity.finish!.getTime() +
                                activity.totalFloatDays * MS_PER_DAY
                            )}
                            y1={y + ROW_HEIGHT / 2 - 4}
                            y2={y + ROW_HEIGHT / 2 - 4}
                            stroke="var(--text-muted)"
                            strokeWidth="1"
                            strokeDasharray="2 2"
                          />
                        )}

                        {activity.isMilestone ? (
                          <path
                            d={`M${startX},${y + ROW_HEIGHT / 2 - 6} l6,6 l-6,6 l-6,-6 z`}
                            fill={
                              activity.isComplete ? 'var(--status-neutral)' : barColour
                            }
                          />
                        ) : (
                          <>
                            <rect
                              x={startX}
                              y={y + 6}
                              width={barWidth}
                              height={ROW_HEIGHT - 14}
                              fill={barColour}
                              opacity={activity.isComplete ? 0.5 : 0.9}
                              rx={1}
                            />
                            {/* Progress fill */}
                            {activity.percentComplete > 0 && !activity.isComplete && (
                              <rect
                                x={startX}
                                y={y + 6}
                                width={(barWidth * activity.percentComplete) / 100}
                                height={ROW_HEIGHT - 14}
                                fill="var(--status-pass)"
                                opacity={0.55}
                                rx={1}
                              />
                            )}
                          </>
                        )}
                      </g>
                    </g>
                  );
                })}
              </svg>
            </div>
          </>
        )}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
        <Swatch colour="var(--mr-vivid-blue)" label="Critical" />
        <Swatch colour="var(--data-4)" label="Non-critical" />
        <Swatch colour="var(--status-fail)" label="Negative float" />
        <Swatch colour="var(--status-neutral)" label="Complete" />
        <span className="flex items-center gap-1.5">
          <svg width="18" height="6" aria-hidden="true">
            <line x1="0" y1="3" x2="18" y2="3" stroke="var(--text-muted)" strokeWidth="1" strokeDasharray="2 2" />
          </svg>
          Float tail
        </span>
        <span className="flex items-center gap-1.5">
          <svg width="18" height="6" aria-hidden="true">
            <rect y="1.5" width="18" height="3" rx="1" fill="var(--text-muted)" opacity="0.5" />
          </svg>
          Baseline
        </span>
        <span className="ml-auto tnum">{dated.length} activities</span>
      </div>
    </div>
  );
}

function Swatch({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colour }} />
      {label}
    </span>
  );
}
